import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import util from "../util/util";
import LoadingIndicatorHeader from "../loading-indicator/loading-indicator-header";

export default function ConflictFormSchedule(props) {
  // indicator for when the component has loaded
  const [loaded, setLoaded] = useState(false);
  // the Schedule to display (includes Days, Timeslots, Shows, Interstitials and
  // the Day/Timeslot/Interstitial in Conflict)
  const [schedule, setSchedule] = useState([]);
  // this Component renders only when the 'schedule' or the 'sunday' changes (but they
  // are coupled together, so it's enough if we re-render it based on the 'schedule')
  // the other tricky part is that we want to re-render it only in some cases, and not
  // in others - so using some variables to store in memory values we need to compare;
  // on the case when the week and the timeslot don't change, but only the day changes
  // there's no need to do the 2 backend calls (Schedule+Interstitials) as we already
  // have the data, so we only need to update the 'timeslot in conflict', but for this
  // we also need a copy of the 'schedule' (as using it directly, because it's an array,
  // and because we re-evaluate (re-write) the 'timeslot in conflict' part every time,
  // react sees it as modified and keeps re-rendering the component, infinitely)
  // so, we use the 'schedule' hook to render the component, and the below refs to
  // compare old to new values, and re-render the component only in some cases
  // !!! important - as we cannot update the 'oldSchedules' and the 'schedules' hooks
  // directly, as doing this 'let updatedSchedule = oldSchedule.current;' and then
  // 'setSchedule(updatedSchedule);' would only copy the references, and the component
  // won't get re-rendered, we need a new array (with spread, map,..) to copy the values
  // from 'oldSchedule.current' ('let updatedSchedule = [...oldSchedule.current];'), and
  // then update the state with the newly copied array ('setSchedule(updatedSchedule);')
  // in order for the Component to get re-rendered
  let sunday = useRef(``);
  let timeslot = useRef(``);
  let oldSchedule = useRef([]);

  // Note on why/when we need to include the called function ('getScheduleByWeek()' in
  // this case), inside the 'useEffect' function:
  // - when the called function ('getScheduleByWeek') uses any of the 'deps' of
  // 'useEffect' ('props.day' or 'props.timeslot' in this case)
  // - when the called function ('getScheduleByWeek') calls some other external
  // functions ('setEmptySchedule', 'processData' and 'findTheConflict' in our case)
  // - the second case could be bypassed by adding this external functions inside the
  // called function ('getScheduleByWeek')
  // - the first case can be bypassed by extracting the 'deps' into separate variables
  // and supply them ar parameters to the called function
  useEffect(() => {
    // get the schedule data to display (one week at a time);
    const getScheduleByWeek = () => {
      // new/current day of the Conflict
      const newDay = props.day;
      // new/current timeslot of the Conflict
      const newTimeslot = props.timeslot;
      // starting day for the schedule (convert whatever day was passed into the Sunday
      // for that week)
      const newSunday = util.returnSunday(
        newDay.format(util.dateFormatComputer)
      );
      // initially empty array of day/timeslots, later to be populated with show/pods
      let updatedSchedule = [...oldSchedule.current];

      // only updating the state, if the week or the timeslot changes
      if (newSunday !== sunday.current || newTimeslot !== timeslot.current) {
        // the Empty Schedule that is going to be filled with Schedules
        // and Interstitials by the 'getScheduleByWeek' method
        updatedSchedule = setEmptySchedule(newSunday, newTimeslot);

        // get this week's show data;
        util
          .getScheduleByWeek(newSunday)
          .then(data => {
            updatedSchedule = processData(data, updatedSchedule);

            // then this week's interstitial data;
            return util.getInterstitialsByWeek(newSunday);
          })
          .then(data => {
            updatedSchedule = processData(data, updatedSchedule);

            // now that the schedule is ready to display, find the conflict
            updatedSchedule = findTheConflict(
              updatedSchedule,
              newDay,
              newTimeslot
            );

            // updating the refs, for future comparisons
            sunday.current = newSunday;
            timeslot.current = newTimeslot;
            oldSchedule.current = updatedSchedule;

            // updating the 'schedule' in the state, for re-rendering
            setSchedule(updatedSchedule);

            // now the final updated schedule can be shown
            setLoaded(true);
          });
      } else {
        // if only the 'day' changes, no need to redo the backend calls
        // only update the 'conflict' timeslot and update the state with the 'schedule'
        updatedSchedule = findTheConflict(updatedSchedule, newDay, newTimeslot);
        oldSchedule.current = updatedSchedule;
        setSchedule(updatedSchedule);
      }
    };

    getScheduleByWeek();
  }, [props.day, props.timeslot]);

  const setEmptySchedule = (newSunday, newTimeslot) => {
    const emptySchedule = [];

    const createRow = (newTimeslot, newSunday) => {
      const days = [];

      // create empty day values for each time slot;
      for (let i = 0; i < 7; i = i + 1) {
        days.push({
          date: moment(newSunday)
            .add(i, `days`)
            .format(util.dateFormatComputer),
          content: null,
          selected: false
        });
      }

      // create the `table row` for each time slot;
      return {
        days: days,
        timeslot: newTimeslot
      };
    };

    // loop through the 24 hours of the day;
    for (let hour = 0; hour <= 23; hour = hour + 1) {
      const hourFormatted = hour < 10 ? `0${hour}` : hour;

      // loop through the hour and half hour slot;
      // the 28 and 58 are placeholders for the associated pods (as interstitials);
      [`00`, `28`, `30`, `58`].forEach(time => {
        // create the `table row` for each time slot;
        emptySchedule.push(createRow(`${hourFormatted}:${time}`, newSunday));
      });
    }

    // find the timeslot we care about;
    // we only want to show the conflict timeslot and the timeslow before and after it;
    let index = 1;

    emptySchedule.forEach((timeslot, i) => {
      if (timeslot.timeslot === newTimeslot) {
        index = i;
      }
    });

    // setting the Schedule as an empty array to later be populated with show/pod data;
    // the schedule is trimmed down to the timeslot specified;
    return emptySchedule.splice(index - 1, 3);
  };

  const processData = (data, schedule) => {
    data.forEach(showOrInterstitial => {
      const showOrIntAirtime = moment(showOrInterstitial.airtime);
      const showOrIntTimeslot = showOrIntAirtime.format(`HH:mm`);
      const showOrIntDate = showOrIntAirtime.format(util.dateFormatComputer);

      // find the timeslot (if it exists);
      schedule.forEach((slot, i) => {
        if (slot.timeslot === showOrIntTimeslot) {
          // loop through the days and find a match (if it exists);
          slot.days.forEach((day, j) => {
            if (day.date === showOrIntDate) {
              // add the show or interstitial to the timeslot;
              schedule[i].days[j].content = showOrInterstitial.title
                ? showOrInterstitial.title
                : showOrInterstitial.pod.name;
            }
          });
        }
      });
    });

    return schedule;
  };

  const findTheConflict = (schedule, day, timeslot) => {
    schedule.forEach(slot => {
      // set the flag to 'false', on all the non-matching timeslots, on the existing
      // schedule (reusing the schedule, if only the day has changed (but not the
      // week and the timeslot), in order to avoid a non-necessary backend call)
      slot.days.forEach(day => {
        day.selected = false;
      });

      if (slot.timeslot === timeslot) {
        const index = Number(moment(day).format(`d`));

        // set the flag to 'true', to turn the highlight class on
        slot.days[index].selected = true;
      }
    });

    return schedule;
  };

  if (loaded === false) {
    return <LoadingIndicatorHeader />;
  } else {
    return (
      <div>
        <h4 className="page__header">Current Schedule</h4>

        <div className="table-responsive">
          <table className="table table-striped table-bordered table-hover">
            <colgroup>
              <col style={{ width: `12.5%` }}></col>
              <col style={{ width: `12.5%` }}></col>
              <col style={{ width: `12.5%` }}></col>
              <col style={{ width: `12.5%` }}></col>
              <col style={{ width: `12.5%` }}></col>
              <col style={{ width: `12.5%` }}></col>
              <col style={{ width: `12.5%` }}></col>
              <col style={{ width: `12.5%` }}></col>
            </colgroup>
            <thead>
              <tr>
                <th></th>
                {util.days.map((day, i) => {
                  const date = moment(sunday.current).add(i, `days`);

                  return (
                    <th key={i}>{date.format(util.dateFormatHumanShort)}</th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {schedule.map((row, i) => (
                <tr key={i}>
                  <th>{row.timeslot}</th>
                  {row.days.map((day, j) => (
                    <td key={j} className={day.selected ? `danger` : null}>
                      {day.content}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
}

ConflictFormSchedule.propTypes = {
  day: PropTypes.object,
  timeslot: PropTypes.string
};
